import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconName, SCREENS, ScreenId } from './data';
import { useLanding } from './i18n';

const ICONS: Record<IconName | string, ReactNode> = {
  chart: <path d="M6 20V11M12 20V5M18 20v-6" />,
  ppt: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8 20h8" /></>,
  pdf: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 15h6" /></>,
  doc: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
  mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>,
  code: <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14" />,
  translate: <path d="M4 5h8M8 3v2M5.5 5c.8 3 3 5.5 6 7M10.5 5c-.8 3-3 5.5-6 7M12.5 21l4.5-10 4.5 10M14 17.5h6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><path d="M17.5 14v7M14 17.5h7" /></>,
  grid4: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  building: <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />,
  monitor: <><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></>,
  server: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
  chip: <><rect x="5" y="5" width="14" height="14" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  shieldCheck: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,
  offline: <path d="M1 1l22 22M16.7 11.1A10.9 10.9 0 0 1 19 12.5M5 12.5a10.9 10.9 0 0 1 5.2-2.9M10.7 5.1A16 16 0 0 1 22.6 9M1.4 9a16 16 0 0 1 4.3-2.8M8.5 16.1a6 6 0 0 1 6.9 0M12 20h.01" />,
  book: <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z" />,
  clip: <path d="M21.4 11.1l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  users: <><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.1a4 4 0 0 1 0 7.8M21 21v-2a4 4 0 0 0-3-3.9" /></>,
  clock: <><path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="9" /></>,
  fileCheck: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 15l2 2 4-4" /></>,
  image: <><circle cx="13.5" cy="6.5" r="2.5" /><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7M21 15l-5-5L5 21" /></>,
  wrench: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z" />,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></>,
};

export function Icon({ name, width = 2.2 }: { name: IconName | string; width?: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {ICONS[name]}
    </svg>
  );
}

export const Sparkle = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0c.6 5.6 3.4 9.4 12 12-8.6 2.6-11.4 6.4-12 12-.6-5.6-3.4-9.4-12-12 8.6-2.6 11.4-6.4 12-12z" />
  </svg>
);

export const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M7 4.5l13 7.5-13 7.5z" /></svg>
);

/**
 * Full-size view of a screenshot. The page shows screens at ~40% of their real width, so their
 * text is hard to read; clicking one opens it here at up to its native 1560px width.
 * Arrow keys step through a set; Esc or a click outside closes it.
 */
export function Zoom({ images, index, onIndex, onClose }: {
  images: { src: string; alt: string }[];
  index: number;
  onIndex?: (i: number) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (onIndex && e.key === 'ArrowRight') onIndex((index + 1) % images.length);
      if (onIndex && e.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [index, images.length, onIndex, onClose]);
  const img = images[index];
  // Rendered at the page root: an ancestor with a transform (e.g. the .lp-scrub scroll effect)
  // would otherwise trap this fixed overlay inside that box instead of covering the window.
  const host = (document.querySelector('.lp') as HTMLElement | null) ?? document.body;
  return createPortal(
    <div className="zoom" role="dialog" aria-modal="true" aria-label={img.alt} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <img src={img.src} alt={img.alt} />
      <button className="x" onClick={onClose} aria-label="Close">×</button>
      {onIndex && images.length > 1 && (
        <div className="zoom-steps">
          {images.map((im, i) => (
            <button key={im.src} aria-pressed={i === index} aria-label={`${i + 1}`} onClick={() => onIndex(i)} />
          ))}
        </div>
      )}
    </div>,
    host,
  );
}

/**
 * A real app screen shown on a MacBook (drawn in CSS). Static: no autoplay or camera moves —
 * visitors switch steps with the numbered buttons under the laptop.
 */
const STEP_MS = 5000;

export function ScreenView({ id, className = '' }: { id: ScreenId; className?: string }) {
  const { t } = useLanding();
  const screen = SCREENS[id];
  const copy = t.screens[id];
  const steps = screen?.steps.length ?? 0;
  // Multi-step screens play through on their own (5s per step) while visible, starting at step 1.
  const [active, setActive] = useState(steps > 1 ? 0 : (screen?.initial ?? 0));
  const [zoomed, setZoomed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [userPicked, setUserPicked] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = root.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const playing = steps > 1 && visible && !hovered && !zoomed && !userPicked && !reduced;
  useEffect(() => {
    if (!playing) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % steps), STEP_MS);
    return () => window.clearTimeout(id);
  }, [playing, active, steps]);
  if (!screen) return null;
  const step = screen.steps[active];
  return (
    <div
      ref={root}
      className={`reel device ${className}${playing ? ' playing' : ''}`}
      style={{ ['--step-ms' as string]: `${STEP_MS}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="mbp">
        <div className="mbp-lid">
          <div className="mbp-screen">
            <button type="button" className="rc zoomable" onClick={() => setZoomed(true)} aria-label="View full size">
              {screen.steps.map((st, i) => (
                <img
                  key={st.img}
                  className={i === active ? 'on' : ''}
                  src={`/landing/${st.img}.jpg`}
                  alt={`${screen.title} — ${st.title} — ${copy[i]?.caption ?? ''}`}
                  loading="lazy"
                  decoding="async"
                />
              ))}
              <span className="zoom-hint" aria-hidden><ZoomIcon /></span>
            </button>
          </div>
        </div>
        <div className="mbp-base" aria-hidden />
      </div>
      <div className="rfoot">
        <div className="rcap">
          <i style={{ background: step.color }} />
          <b>{step.title}</b>
          <span>{copy[active]?.caption}</span>
        </div>
        {screen.steps.length > 1 && (
          <div className="rsteps">
            {screen.steps.map((st, i) => (
              <button key={st.img} type="button" aria-pressed={i === active} onClick={() => { setUserPicked(true); setActive(i); }}>
                <em>{i + 1}</em>
                {copy[i]?.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {zoomed && (
        <Zoom
          images={screen.steps.map((st, i) => ({ src: `/landing/${st.img}.jpg`, alt: `${screen.title} — ${copy[i]?.caption ?? st.title}` }))}
          index={active}
          onIndex={(i) => { setUserPicked(true); setActive(i); }}
          onClose={() => setZoomed(false)}
        />
      )}
    </div>
  );
}

export const ZoomIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
    <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5M11 8.5v5M8.5 11h5" />
  </svg>
);
